import { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
  Channel,
  ChannelEvent,
  ChannelEventData,
  GroupChannel,
  NotificationChannel,
  RequestChannel,
} from 'src/services/notificationService/channelsService/types';
import { useBindToChannelLazily } from './useBindToChannelLazily';
import type { GroupId } from 'src/services/groupService/models/group.model';
import useActiveUserId from 'services/user/useActiveUserId';

type Options = {
  /**
   * ensure only 1 listener function is bound to the event globally
   */
  enforceSingleListener?: true;
  /**
   * wait for wrapping logic to enable
   */
  enabled?: boolean;
};

function useBindToChannelEvent<
  C extends Channel,
  E extends ChannelEvent<C>,
  Data extends ChannelEventData<C, E> = ChannelEventData<C, E>
>(
  // channel can be nullish in some cases, so we handle that in this hook and try to limit exposure to this option in the wrapping hooks
  channel: C | null,
  event: E,
  listener: (args: { event: E; data: Data }) => void,
  options?: Options
) {
  const bindEventListener = useBindToChannelLazily();

  // Stable listener identity across renders — prevents re-binding on every
  // parent render. Without this, inline closures cause spurious rebinds.
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  // Stable per-component UUID. A new UUID on every render would cause the
  // prior listener to orphan inside useBindToChannelLazily (the channel-
  // level listener map retains the old entry forever).
  const listenerIdRef = useRef<string | null>(null);
  if (listenerIdRef.current == null) {
    listenerIdRef.current = uuidv4();
  }

  useEffect(() => {
    if (channel == null) {
      return;
    }

    if (options?.enabled === false) {
      return;
    }

    const listenerId = listenerIdRef.current!;

    const boundEvent = bindEventListener(channel, {
      listenerId,
      enforceSingleListener: options?.enforceSingleListener ?? false,
      eventName: event,
      listener: (data) => listenerRef.current({ event, data: data as Data }),
    });

    // Unbind on every cleanup (effect re-run AND unmount). The previous
    // componentWillUnmount gate skipped unbind on re-run, which orphaned
    // listeners under their old (channel, event) key whenever a caller
    // switched channels mid-life — those orphans received events forever.
    return () => {
      boundEvent.unbind();
    };
  }, [
    bindEventListener,
    event,
    channel,
    options?.enforceSingleListener,
    options?.enabled,
  ]);
}

export function useBindToGroupEvent<
  E extends ChannelEvent<GroupChannel>,
  Data extends ChannelEventData<GroupChannel, E> = ChannelEventData<
    GroupChannel,
    E
  >
>(
  groupId: GroupId,
  event: E,
  listener: (args: { event: E; data: Data }) => void,
  options?: Options
) {
  return useBindToChannelEvent<GroupChannel, E, Data>(
    `private-group-${groupId}`,
    event,
    listener,
    options
  );
}
export function useBindToNotificationEvent<
  E extends ChannelEvent<NotificationChannel>,
  Data extends ChannelEventData<NotificationChannel, E> = ChannelEventData<
    NotificationChannel,
    E
  >
>(
  event: E,
  listener: (args: { event: E; data: Data }) => void,
  options?: Options
) {
  const activeUserId = useActiveUserId();
  return useBindToChannelEvent<NotificationChannel, E, Data>(
    activeUserId != null ? `private-notifications-${activeUserId}` : null,
    event,
    listener,
    options
  );
}
export function useBindToRequestsEvent<
  E extends ChannelEvent<RequestChannel>,
  Data extends ChannelEventData<RequestChannel, E> = ChannelEventData<
    RequestChannel,
    E
  >
>(
  event: E,
  listener: (args: { event: E; data: Data }) => void,
  options?: Options
) {
  const activeUserId = useActiveUserId();
  return useBindToChannelEvent<RequestChannel, E, Data>(
    activeUserId != null ? `private-requests-${activeUserId}` : null,
    event,
    listener,
    options
  );
}
